import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Dispatch, Program, UIDispatch} from '../../../../domain/cloud-topology';
import {DispatchService} from '../../../../services/vpp/dispatch.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import moment from 'moment-timezone';
import {ProgramService} from '../../../../services/vpp/program.service';
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {DispatchNewComponent} from "../../dispatch/dispatch-new/dispatch-new.component";
import {cancelStatus, errorStatus, saveStatus, DISPATCH_ACTIVE, DISPATCH_ERROR, DISPATCH_COMPLETE, DISPATCH_PENDING} from "../../../../domain/constants";
import {ResourceService} from "../../../../services/resource.service";
import { MatButton } from '@angular/material/button';

export interface DispatchData {
  dispatch: Dispatch;
  programName: string;
  setpoint: number;
  incentive: number;
  penalty: number;
  startTimeUtc: string;
  endTimeUtc: string;
}
@Component({
    selector: 'app-program-dispatches-index',
    templateUrl: './program-dispatches-index.component.html',
    styleUrls: ['./program-dispatches-index.component.css'],
    standalone: false
})
export class ProgramDispatchesIndexComponent implements OnInit, OnChanges, OnDestroy {

  @Input() programs: Program[];
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  dispatches: Dispatch[] = [];
  dispatchTableData: DispatchData[] = [];
  requestsCompleted: boolean = false;
  DISPATCH_ACTIVE = DISPATCH_ACTIVE;
  DISPATCH_COMPLETE = DISPATCH_COMPLETE;
  DISPATCH_ERROR = DISPATCH_ERROR;
  DISPATCH_PENDING = DISPATCH_PENDING;
  dispatchStatusField = 'status';
  dateFormatStr: string = 'MM/dd/yyyy HH:mm';
  displayedColumns: string[] = ['status', 'programName', 'setpoint', 'incentive', 'penalty', 'startTimeUtc', 'endTimeUtc'];
  dataSource: MatTableDataSource<DispatchData>;
  pageSize: number = 10;
  private subject$: Subject<void>;

  constructor(private dispatchService: DispatchService,
              private programService: ProgramService,
              private snackBar: MatSnackBar,
              private dialogService: MatDialog) {
    this.dataSource = new MatTableDataSource<DispatchData>();
  }

  ngOnInit(): void {
    this.subject$ = new Subject<void>();
    this.setData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['programs']['currentValue'] !== undefined) {
      this.programs = changes['programs']['currentValue'];
    }
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  private setData() {

    for (const program of this.programs) {
      this.dispatchService.getProgramDispatches(program, 'UTC').subscribe(data => {

        this.dispatches = this.dispatches.concat(data);
        if (this.programs.indexOf(program) == (this.programs.length - 1)) {
          this.sortDispatches();
          this.requestsCompleted = true;
          this.dataSource = new MatTableDataSource<DispatchData>(this.setDispatchTableData());
          this.dataSource.sortingDataAccessor = (dispatchData, property) => {
            if (property == this.dispatchStatusField) {
              return dispatchData.dispatch.status;
            } else {
              return dispatchData[property];
            }
          };
          setTimeout(() => {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
          });
        }
      });
    }
  }

  private sortDispatches() {
    let statusSortElements = [];

    statusSortElements = statusSortElements.concat(this.dispatches.filter(dispatch => {
      return dispatch.status == 'ERROR' && moment().utc().diff(moment(dispatch.startTimeUtc).utc(), 'hours') < 24;
    }).sort((a, b) =>
      (moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix())));
    statusSortElements = statusSortElements.concat(this.dispatches.filter(dispatch => { return dispatch.status == 'PENDING'}).sort((a, b) =>
      moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix()));
    statusSortElements = statusSortElements.concat(this.dispatches.filter(dispatch => { return dispatch.status == 'ACTIVE'}).sort((a, b) =>
      moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix()));
    statusSortElements = statusSortElements.concat(this.dispatches.filter(dispatch => { return dispatch.status == 'COMPLETE'}).sort((a, b) =>
      moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix()));


    this.dispatches = statusSortElements;
  }

  private setDispatchTableData(): DispatchData[] {
    this.dispatchTableData = [];
    if (this.dispatches) {
      for (const dispatch of this.dispatches) {
        this.dispatchTableData.push({
          dispatch: dispatch,
          programName: this.getDispatchProgram(dispatch).name,
          setpoint: dispatch.setpoint,
          incentive: dispatch.incentive,
          penalty: dispatch.penalty,
          startTimeUtc: dispatch.startTimeUtc,
          endTimeUtc: dispatch.endTimeUtc
        });
      }
      return this.dispatchTableData;
    }
  }

  private getDispatchProgram(dispatch: Dispatch): Program {
    for (const program of this.programs) {
      if (dispatch.targetId === program.id) {
        return program
      }
    }

    return null;
  }

  cleanNumericInput(element, key) {
    return UIDispatch.cleanNumericOutput(element[key]);
  }

  addDispatch(button: MatButton) {
    (button._elementRef.nativeElement as HTMLButtonElement).blur();
    const dialogRef = this.dialogService.open(DispatchNewComponent, {
      width: '1100px',
      data: {
        dispatch: {
          id: null,
          type: null,
          setpoint: 0,
          duration: null,
          incentive: 0,
          penalty: 0,
        },
        programs: this.programs,
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data?.status == saveStatus) {
        const dispatchForm = data['dispatch'];
        const dateStr = ResourceService.convertBootstrapDate(dispatchForm);
        let startTimeUtc = moment.tz(`${dateStr}`, data['timezone']).utc();
        let dispatchPayload = {
          id: null,
          startTimeUtc: startTimeUtc.format(),
          setpoint: dispatchForm['setpoint'],
          incentive: dispatchForm['incentive'],
          penalty: dispatchForm['penalty'],
          duration: `PT${dispatchForm['duration']}${dispatchForm['durationType']}`,
          type: data.program.programType,
        };

        this.dispatchService.postProgramDispatch(data['program'], dispatchPayload).subscribe(dispatch => {
          let startTime = moment(dispatch.startTimeUtc);
          let endTime = moment(dispatch.endTimeUtc);

          if (!startTime.isDST()) {
            startTime.add(1, 'hour');
          }

          if (!endTime.isDST()) {
            endTime.add(1, 'hour');
          }

          this.dataSource.data.unshift({
            dispatch: dispatch,
            programName: this.getDispatchProgram(dispatch).name,
            setpoint: dispatch.setpoint,
            incentive: dispatch.incentive,
            penalty: dispatch.penalty,
            startTimeUtc: dispatch.startTimeUtc,
            endTimeUtc: dispatch.endTimeUtc
          });

          this.dataSource.data = [...this.dataSource.data];
          this.snackBar.open("Dispatch has been successfully created", "Close", ResourceService.getDefaultSnackBarConfig());
        }, () => {
          this.snackBar.open("Problem creating dispatch", "Close", {
            duration: 3000,
            horizontalPosition: "right",
            verticalPosition: "bottom"
          });
        });
        return;
      }

      if (data?.status == errorStatus) {
        this.snackBar.open("You can not add a dispatch to a coincident peak program.", "Close", ResourceService.getDefaultSnackBarConfig());
        return;
      }

      if (data?.status == cancelStatus) {
        this.snackBar.open("Changes have been discarded.", "Close",  ResourceService.getDefaultSnackBarConfig());
        return;
      }
    })
  }

  removeDispatch(tableObject) {
    if (!window.confirm('Are you sure you want to remove the dispatch?')) {
      this.snackBar.open("Changes have been discarded.", "Close", ResourceService.getDefaultSnackBarConfig());
      return;
    }


    let program = this.getDispatchProgram(tableObject.dispatch);
    this.dispatchService.deleteProgramDispatch(program, tableObject.dispatch).pipe(takeUntil(this.subject$)).subscribe( () => {
      const dispatches = [];
      for (const dispatchElement of this.dispatches) {
        if (dispatchElement.id !== tableObject.dispatch.id) {
          dispatches.push(dispatchElement);
        }
      }
      this.dispatches = dispatches;
      this.setDispatchTableData();
      this.dataSource.data = this.dispatchTableData;
      this.snackBar.open("Dispatch has been deleted.", "Close", ResourceService.getDefaultSnackBarConfig());
    }, (err) => {
      console.error(`Problem deleting dispatch reason: ${err}`);
      this.snackBar.open("There was a problem removing the dispatch", "Close", ResourceService.getDefaultSnackBarConfig());
    });
  }

}
